/**
 * AP-8 — FM-WIN-04: DM1 Monitor.
 * Datenquelle ist `getSnapshot('dm1', <Dm01Version-Index>)`; der WASM-Kern
 * dekodiert dabei aus dem Rohpayload neu, damit die gewaehlte DM01-Version
 * gegenueber dem festen SAE-Layout gewinnt (wie Dm1Window::Refresh()).
 */

import './viewsB.css';
import { createDataTable } from './dataTable';
import { createFloatingPanel } from './filters/FloatingPanel';
import { showDetailFields, type DetailField } from './Dm1Detail';
import { createButton, createCheckbox, createLabel } from '../ui/controls';
import type { AppStore } from '../app/store';
import type { CanConnection } from '../app/connection';

/** Anzeigenamen der Vendor-Layouts, Reihenfolge = `Dm01VersionNames()`. */
const DM01_VERSIONS = ['V1', 'V2', 'V3', 'V4', 'MAN'] as const;
const DM01_DEFAULT_INDEX = 2; // "Vorgabe Version 3" (FM-WIN-04)

/** `EngineTypeNames()` — reine Beschriftung, aendert die SPN-Dekodierung nicht. */
const ENGINE_TYPES = [
  'Generic (SAE J1939)',
  'Daimler ADM2/ADM3',
  'Caterpillar C4/C6',
  'Caterpillar C7/C32',
  'Deutz EMR2',
  'Deutz EMR3',
  'Deutz EMR4',
  'Volvo EDC4',
  'Perkins',
  'Cummins',
] as const;

const REFRESH_INTERVAL_MS = 100;
const LAMP_BLINK_INTERVAL_MS = 125;

interface Dtc {
  spn: number;
  fmi: number;
  oc: number;
  cm: boolean;
  spnName: string;
  fmiName: string;
}

interface LampSet {
  mil: string;
  redStop: string;
  amberWarning: string;
  protect: string;
  flash: { mil: string; redStop: string; amberWarning: string; protect: string };
}

interface Dm1Node {
  sa: number;
  count: number;
  lamps: LampSet;
  dtcs: Dtc[];
}

interface Dm1Snapshot {
  nodes: Dm1Node[];
}

type LampKey = 'mil' | 'redStop' | 'amberWarning' | 'protect';

const LAMP_LABELS: ReadonlyArray<[LampKey, string, string]> = [
  ['mil', 'MIL', 'cm-lamp--mil'],
  ['redStop', 'Red Stop', 'cm-lamp--red-stop'],
  ['amberWarning', 'Amber Warning', 'cm-lamp--amber'],
  ['protect', 'Protect', 'cm-lamp--protect'],
];

/** `CombineLampState()`: An schlaegt Aus, Aus schlaegt "nicht verfuegbar". */
function combineLamp(current: string | undefined, next: string): string {
  if (current === undefined) return next;
  if (current === 'On' || next === 'On') return 'On';
  if (current === 'Off' || next === 'Off') return 'Off';
  return current;
}

function combineFlash(current: string | undefined, next: string): string {
  if (current === undefined) return next;
  return current === 'No Flash' ? next : current;
}

function hex2(value: number): string {
  return value.toString(16).toUpperCase().padStart(2, '0');
}

export function createDm1View(store: AppStore, connection: CanConnection): { show(): void } {
  const panel = createFloatingPanel('DM1 Monitor');
  panel.element.classList.add('cm-panel-window--wide');

  const root = document.createElement('div');
  root.className = 'cm-viewb';

  // -- Bedienzeile ----------------------------------------------------------
  const controls = document.createElement('div');
  controls.className = 'cm-viewb__row';

  const saFilter = document.createElement('select');
  saFilter.className = 'cm-combo';
  let saOptionsKey = '';

  const engineType = document.createElement('select');
  engineType.className = 'cm-combo';
  for (const name of ENGINE_TYPES) {
    const option = document.createElement('option');
    option.textContent = name;
    engineType.append(option);
  }

  const dm01Version = document.createElement('select');
  dm01Version.className = 'cm-combo';
  DM01_VERSIONS.forEach((name, index) => {
    const option = document.createElement('option');
    option.value = String(index);
    option.textContent = name;
    dm01Version.append(option);
  });
  dm01Version.value = String(DM01_DEFAULT_INDEX);

  const decodeCheck = createCheckbox('Decode DM1', { onChange: () => void refresh(true) });
  const clearBtn = createButton('Clear', { onClick: () => clearCounts() });

  controls.append(
    createLabel('Source Address:'),
    saFilter,
    createLabel('Engine Type:'),
    engineType,
    createLabel('DM01 Version:'),
    dm01Version,
    decodeCheck.element,
    clearBtn,
  );

  // -- Lampenzeile ----------------------------------------------------------
  const lampRow = document.createElement('div');
  lampRow.className = 'cm-lamps';
  const lampElements = new Map<LampKey, HTMLElement>();
  for (const [key, label, modifier] of LAMP_LABELS) {
    const lamp = document.createElement('div');
    lamp.className = `cm-lamp ${modifier}`;
    const dot = document.createElement('span');
    dot.className = 'cm-lamp__dot';
    const text = document.createElement('span');
    text.textContent = label;
    lamp.append(dot, text);
    lampRow.append(lamp);
    lampElements.set(key, lamp);
  }

  const table = createDataTable({
    columns: ['SA', 'Cnt', 'SPN', 'FMI', 'OC', 'CM'],
    onSelect: () => undefined,
    emptyText: 'no DM1 traffic yet',
  });

  root.append(controls, lampRow, table.element);
  panel.body.append(root);

  // -- Zustand --------------------------------------------------------------
  /** `clearedDm1Counts_`: je SA der Zaehlerstand beim letzten `Clear`. */
  const clearedCounts = new Map<number, number>();
  /** Zeilenmetadaten fuer den Doppelklick (Dm1Window::rows_). */
  let rowMeta: Array<{ sa: number; dtc: Dtc | null }> = [];
  let lampSummary: Partial<Record<LampKey, string>> = {};
  let flashSummary: Partial<Record<LampKey, string>> = {};
  let lastRefresh = 0;
  let refreshQueued = false;
  let blinkPhaseDark = false;
  let blinkTimer: ReturnType<typeof setInterval> | null = null;

  function selectedSa(): number | null {
    return saFilter.value === 'all' ? null : Number(saFilter.value);
  }

  function clearCounts(): void {
    for (const meta of rowMeta) clearedCounts.set(meta.sa, Number.MAX_SAFE_INTEGER);
    void refresh(true);
  }

  function updateSaOptions(nodes: readonly Dm1Node[]): void {
    const sas = [...new Set(nodes.map((node) => node.sa))].sort((a, b) => a - b);
    const key = sas.join(',');
    if (key === saOptionsKey) return;
    saOptionsKey = key;
    const previous = saFilter.value;
    saFilter.replaceChildren();
    const all = document.createElement('option');
    all.value = 'all';
    all.textContent = 'All';
    saFilter.append(all);
    for (const sa of sas) {
      const option = document.createElement('option');
      option.value = String(sa);
      option.textContent = hex2(sa);
      saFilter.append(option);
    }
    // Auswahl nicht auf "All" zuruecksetzen, solange sie noch existiert.
    saFilter.value = [...saFilter.options].some((option) => option.value === previous) ? previous : 'all';
  }

  function applyLamps(): void {
    for (const [key, , ] of LAMP_LABELS) {
      const element = lampElements.get(key);
      if (element === undefined) continue;
      const state = lampSummary[key] ?? 'N/A';
      const flash = flashSummary[key] ?? 'No Flash';
      const on = state === 'On';
      element.classList.toggle('cm-lamp--on', on);
      element.classList.toggle('cm-lamp--dark', on && flash !== 'No Flash' && blinkPhaseDark);
    }
  }

  async function refresh(force = false): Promise<void> {
    if (!panel.visible) return;
    const now = performance.now();
    if (!force && now - lastRefresh < REFRESH_INTERVAL_MS) {
      if (!refreshQueued) {
        refreshQueued = true;
        setTimeout(() => {
          refreshQueued = false;
          void refresh(true);
        }, REFRESH_INTERVAL_MS);
      }
      return;
    }
    lastRefresh = now;

    let snapshot: Dm1Snapshot;
    try {
      snapshot = (await connection.getSnapshot('dm1', Number(dm01Version.value))) as Dm1Snapshot;
    } catch {
      return; // Kein Dekoder geladen — Fenster bleibt leer statt zu stoeren.
    }

    updateSaOptions(snapshot.nodes);

    const saFilterValue = selectedSa();
    const rows: string[][] = [];
    const meta: Array<{ sa: number; dtc: Dtc | null }> = [];
    lampSummary = {};
    flashSummary = {};

    for (const node of snapshot.nodes) {
      if (saFilterValue !== null && node.sa !== saFilterValue) continue;
      const cleared = clearedCounts.get(node.sa);
      if (cleared !== undefined && node.count <= cleared) continue;

      for (const [key] of LAMP_LABELS) {
        lampSummary[key] = combineLamp(lampSummary[key], node.lamps[key]);
        flashSummary[key] = combineFlash(flashSummary[key], node.lamps.flash[key]);
      }

      const sa = hex2(node.sa);
      const count = String(node.count);
      if (node.dtcs.length === 0) {
        rows.push([sa, count, '-', '-', '-', '-']);
        meta.push({ sa: node.sa, dtc: null });
        continue;
      }
      for (const dtc of node.dtcs) {
        let spnText = String(dtc.spn);
        let fmiText = String(dtc.fmi);
        if (decodeCheck.input.checked) {
          // Beim Dekodieren IMMER etwas anhaengen, sonst bleibt unklar, ob
          // ueberhaupt dekodiert wurde (Dm1Window.cpp:829).
          spnText += dtc.spnName === '' ? ' - (unknown SPN)' : ` - ${dtc.spnName}`;
          if (dtc.fmiName !== '') fmiText += ` - ${dtc.fmiName}`;
        }
        rows.push([sa, count, spnText, fmiText, String(dtc.oc), dtc.cm ? '1' : '0']);
        meta.push({ sa: node.sa, dtc });
      }
    }

    rowMeta = meta;
    table.setRows(rows);
    applyLamps();
  }

  function openDetail(row: number): void {
    const meta = rowMeta[row];
    if (meta === undefined) return;
    const saText = `0x${hex2(meta.sa)}`;
    const fields: DetailField[] = [{ label: 'Source Address', value: saText }];
    if (meta.dtc === null) {
      fields.push({ label: 'Status', value: 'No active faults' });
    } else {
      fields.push(
        { label: 'SPN', value: String(meta.dtc.spn) },
        { label: 'SPN Name', value: meta.dtc.spnName === '' ? '(not in generic table)' : meta.dtc.spnName },
        { label: 'FMI', value: String(meta.dtc.fmi) },
        { label: 'FMI Name', value: meta.dtc.fmiName },
        { label: 'Occurrence Count', value: String(meta.dtc.oc) },
        { label: 'Conversion Method (CM)', value: meta.dtc.cm ? '1' : '0' },
      );
    }
    fields.push({ label: 'Engine Type', value: engineType.value });
    showDetailFields(`DM1 Detail - SA ${saText}`, fields);
  }

  table.element.addEventListener('dblclick', (event) => {
    const row = (event.target as HTMLElement | null)?.closest('tr');
    if (row instanceof HTMLTableRowElement && row.parentElement?.tagName === 'TBODY') {
      openDetail(row.sectionRowIndex);
    }
  });

  for (const control of [saFilter, dm01Version]) {
    control.addEventListener('change', () => void refresh(true));
  }

  store.on('frames:appended', () => void refresh());
  store.on('frames:cleared', () => {
    clearedCounts.clear();
    void refresh(true);
  });

  return {
    show(): void {
      panel.show();
      if (blinkTimer === null) {
        blinkTimer = setInterval(() => {
          blinkPhaseDark = !blinkPhaseDark;
          applyLamps();
        }, LAMP_BLINK_INTERVAL_MS);
      }
      void refresh(true);
    },
  };
}
